import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { BeatRow } from "@/components/BeatRow";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Music4, Upload, Loader2 } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { BeatFilters, applyFilters, NO_FILTERS } from "@/components/BeatFilters";
import { toggleFavorite } from "@/lib/favorites";
import { EditBeatDialog } from "@/components/EditBeatDialog";
import { BeatMetaFields, appendMeta, EMPTY_META } from "@/components/BeatMetaFields";

export default function Beats() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  const load = () => api.get("/beats").then((r) => setBeats(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, [user]);

  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState(NO_FILTERS);
  const { track, close, syncTrack, setQueue } = usePlayer();
  const shown = applyFilters(beats, filters);
  const genres = [...new Set(beats.map((b) => b.genre).filter(Boolean))];
  const [searchParams] = useSearchParams();
  const sharedId = searchParams.get("beat");
  const [highlightId, setHighlightId] = useState(null);
  const queueKey = shown.map((b) => `${b.beat_id}:${b.title}:${b.cover_version}`).join("|");

  useEffect(() => { setQueue(shown); }, [queueKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading || !sharedId) return;
    if (!beats.some((b) => b.beat_id === sharedId)) { toast.error("That beat is no longer available"); return; }
    setHighlightId(sharedId);
    setTimeout(() => document.getElementById(`beat-${sharedId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
    const t = setTimeout(() => setHighlightId(null), 4000);
    return () => clearTimeout(t);
  }, [loading, sharedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSaved = (data) => {
    setBeats((prev) => prev.map((b) => (b.beat_id === data.beat_id ? { ...b, ...data } : b)));
    syncTrack({ ...editing, ...data });
    setEditing(null);
  };

  const fav = (b) => {
    if (!user) return goLogin(b);
    toggleFavorite(b, (on) => setBeats((prev) => prev.map((x) => (x.beat_id === b.beat_id ? { ...x, favorited: on } : x))));
  };

  const goLogin = (b) => {
    localStorage.setItem("post_login_redirect", `/beats?beat=${b.beat_id}`);
    navigate("/login");
  };

  const buy = async (id) => {
    if (!user) return goLogin({ beat_id: id });
    setBuying(id);
    try {
      await api.post(`/beats/${id}/buy`);
      toast.success("Beat secured — it's yours exclusively.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed");
      load();
    } finally { setBuying(null); }
  };

  const removeBeat = async (beat) => {
    if (!window.confirm(`Delete "${beat.title}"? This removes it from the store permanently.`)) return;
    try {
      await api.delete(`/beats/${beat.beat_id}`);
      toast.success("Beat deleted");
      if (track?.beat_id === beat.beat_id) close();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const downloadStems = async (beat) => {
    try {
      const res = await api.get(`/beats/${beat.beat_id}/stems`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = beat.stems_name || `${beat.title}-stems.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download stems");
    }
  };

  return (
    <div className="grain min-h-screen relative">
      <Navbar />
      <section className="px-6 md:px-12 lg:px-20 pt-16 pb-8 relative z-10">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D90429] mb-3">Exclusive Beats</p>
        <h1 className="font-display text-4xl md:text-6xl font-extrabold tracking-tighter max-w-3xl">One beat. One owner.</h1>
        <p className="text-[#A0A0A0] mt-4 max-w-xl">Every beat is sold a single time — once it's gone, it's gone. RON 900 includes the full stems archive.</p>
      </section>

      {user?.role === "admin" && <AdminUpload onUploaded={load} genres={genres} />}
      <EditBeatDialog beat={editing} genres={genres} onClose={() => setEditing(null)} onSaved={onSaved} />

      <section className="px-6 md:px-12 lg:px-20 py-8 relative z-10" data-testid="beats-list">
        {loading ? (
          <p className="text-[#A0A0A0]">Loading beats...</p>
        ) : beats.length === 0 ? (
          <div className="border border-white/10 bg-[#121212] p-16 text-center">
            <Music4 className="h-10 w-10 text-[#D90429] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[#A0A0A0]">No beats listed yet. Check back soon.</p>
          </div>
        ) : (
          <>
          <BeatFilters beats={beats} filters={filters} setFilters={setFilters} signedIn={!!user} />
          <div className="relative border border-white/10 bg-gradient-to-b from-[#141414] to-[#0D0D0D] shadow-[0_30px_80px_-30px_rgba(217,4,41,0.35)]" data-testid="beats-player">
            <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-white/10 bg-black/40">
              <div className="flex items-center gap-2">
                <span className="flex items-end gap-[2px] h-4">
                  {[60, 100, 40, 80].map((h, i) => <span key={h} style={{ height: `${h}%`, animationDelay: `${i * 0.15}s` }} className="w-[3px] bg-[#D90429] animate-pulse" />)}
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/80">Ceinaru Beat Deck</span>
              </div>
              <span className="text-[11px] font-mono text-white/40" data-testid="beats-count">{shown.length} {shown.length === 1 ? "track" : "tracks"}</span>
            </div>
            {shown.length === 0 && (
              <div className="px-5 py-8 text-center" data-testid="beats-no-match">
                <p className="text-sm text-[#A0A0A0]">No beats match these filters.</p>
                <button onClick={() => setFilters(NO_FILTERS)} data-testid="beats-clear-filters"
                  className="mt-3 text-xs font-bold uppercase tracking-wider text-[#D90429] hover:text-white transition-colors">Clear filters</button>
              </div>
            )}
            {shown.map((b, i) => (
              <BeatRow key={b.beat_id} b={b} index={i} user={user} buying={buying}
                onBuy={buy} onDelete={removeBeat} onStems={downloadStems} onEdit={setEditing} onFavorite={fav} highlighted={highlightId === b.beat_id} />
            ))}
          </div>
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}

function AdminUpload({ onUploaded, genres }) {
  const [title, setTitle] = useState("");
  const [audio, setAudio] = useState(null);
  const [stems, setStems] = useState(null);
  const [cover, setCover] = useState(null);
  const [meta, setMeta] = useState(EMPTY_META);
  const coverRef = useRef();
  const [uploading, setUploading] = useState(false);
  const audioRef = useRef();
  const stemsRef = useRef();

  const submit = async () => {
    if (!title || !audio) { toast.error("Title and an .mp3 file are required."); return; }
    const fd = new FormData();
    fd.append("title", title);
    fd.append("audio", audio);
    if (stems) fd.append("stems", stems);
    if (cover) fd.append("cover", cover);
    appendMeta(fd, meta);
    setUploading(true);
    try {
      await api.post("/beats", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Beat uploaded");
      setTitle(""); setAudio(null); setStems(null); setCover(null); setMeta(EMPTY_META);
      if (coverRef.current) coverRef.current.value = "";
      if (audioRef.current) audioRef.current.value = "";
      if (stemsRef.current) stemsRef.current.value = "";
      onUploaded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <section className="px-6 md:px-12 lg:px-20 relative z-10 mb-4">
      <div className="border border-[#D90429]/30 bg-[#1A0306]/40 p-6" data-testid="beat-upload-form">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D90429] mb-4 flex items-center gap-2"><Upload className="h-4 w-4" /> Upload a beat (admin)</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="md:col-span-1">
            <label className="text-xs text-[#A0A0A0] uppercase tracking-wider">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="beat-title-input"
              className="w-full mt-1 bg-[#121212] border border-white/10 focus:border-[#D90429] outline-none px-3 py-2.5 text-sm" placeholder="Midnight Drive" />
          </div>
          <div>
            <label className="text-xs text-[#A0A0A0] uppercase tracking-wider">Audio (.mp3)</label>
            <input ref={audioRef} type="file" accept=".mp3,audio/mpeg" onChange={(e) => setAudio(e.target.files[0])} data-testid="beat-audio-input"
              className="w-full mt-1 text-xs text-[#A0A0A0] file:mr-3 file:border-0 file:bg-[#D90429] file:text-white file:px-3 file:py-2 file:font-bold file:uppercase" />
          </div>
          <div>
            <label className="text-xs text-[#A0A0A0] uppercase tracking-wider">Stems archive (.zip)</label>
            <input ref={stemsRef} type="file" accept=".zip,.rar,application/zip" onChange={(e) => setStems(e.target.files[0])} data-testid="beat-stems-input"
              className="w-full mt-1 text-xs text-[#A0A0A0] file:mr-3 file:border-0 file:bg-white/10 file:text-white file:px-3 file:py-2 file:font-bold file:uppercase" />
          </div>
          <div>
            <label className="text-xs text-[#A0A0A0] uppercase tracking-wider">Cover (jpg/png/webp)</label>
            <input ref={coverRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setCover(e.target.files[0])} data-testid="beat-cover-input"
              className="w-full mt-1 text-xs text-[#A0A0A0] file:mr-3 file:border-0 file:bg-white/10 file:text-white file:px-3 file:py-2 file:font-bold file:uppercase" />
          </div>
          <BeatMetaFields value={meta} onChange={setMeta} genres={genres} prefix="beat" />
          <button onClick={submit} disabled={uploading} data-testid="beat-upload-button"
            className="bg-[#D90429] hover:bg-[#ED2939] text-white px-6 py-2.5 font-bold uppercase tracking-wide text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
          </button>
        </div>
      </div>
    </section>
  );
}
